"use client";

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, type ExternalToast } from "sonner";
import {
  getUserRecordingSettings,
  updateUserRecordingSettings,
  USER_GET_SETTINGS,
} from "@/api/client/user-settings";
import { useSessionContext } from "@/context";
import { showErrorToast } from "@/lib/errorUtils";
import { Setting, SettingType } from "@/types";

// The auto-join setting rarely changes and its endpoint is slow, so serve it
// from cache when returning to the page instead of refetching into a flicker.
const RECORDING_SETTING_STALE_TIME = 5 * 60 * 1000;

type UseRecordingSettingOptions = {
  /** Extra options forwarded to both the success and the error toast. */
  toastOptions?: ExternalToast;
};

/**
 * Reads and writes the "auto-join calendar events" setting.
 *
 * The write is optimistic: the selection moves as soon as the user clicks and
 * only rolls back if the server rejects it. That matters because the PATCH is
 * slow, and it keeps the query cache and the UI as a single source of truth so
 * navigating away and back can't resurrect the previous value.
 */
export const useRecordingSetting = ({
  toastOptions,
}: UseRecordingSettingOptions = {}) => {
  const { userId, isReady } = useSessionContext();
  const queryClient = useQueryClient();

  const queryKey = [USER_GET_SETTINGS, userId];

  const { data, isFetching } = useQuery({
    queryFn: () => getUserRecordingSettings(userId),
    queryKey,
    enabled: isReady,
    staleTime: RECORDING_SETTING_STALE_TIME,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (setting: SettingType) =>
      updateUserRecordingSettings(userId!, { setting }),
    onMutate: async (setting) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Setting>(queryKey);
      queryClient.setQueryData<Setting>(queryKey, (old) =>
        old ? { ...old, setting } : { userId: userId!, setting },
      );
      return { previous };
    },
    onSuccess: (res) => {
      // The PATCH response is the server's own copy of the record, so it can
      // replace the optimistic value without a second (slow) round trip. If it
      // comes back without one, the optimistic value already stands.
      if (res?.setting) {
        queryClient.setQueryData<Setting>(queryKey, res);
      }
      toast("Settings updated", toastOptions);
    },
    onError: (error, _setting, context) => {
      if (context?.previous) {
        queryClient.setQueryData<Setting>(queryKey, context.previous);
      } else {
        // setQueryData ignores an undefined value, so there is nothing to roll
        // back to here (the read failed too). Reset rather than invalidate:
        // invalidation only marks the query stale, and if the refetch fails as
        // well - likely, since the same endpoint just did - the optimistic
        // value would survive as a save that never happened.
        queryClient.resetQueries({ queryKey });
      }
      showErrorToast(error, "settings", undefined, toastOptions);
    },
  });

  const updateSetting = useCallback(
    (setting: SettingType) => {
      // These pages render for a beat before a signed-out user is redirected,
      // and there is no record to patch without an id. Re-picking the option
      // already in effect is a no-op rather than another slow round trip. The
      // effect this replaced made both checks too.
      if (!userId || setting === data?.setting) return;
      mutate(setting);
    },
    [mutate, userId, data?.setting],
  );

  return {
    selectedOption: data?.setting,
    updateSetting,
    /** True only on the initial load, when there is nothing to show yet. */
    isLoading: isFetching && !data,
    isPending,
  };
};
